<template>
  <table>
    <thead>
      <tr>
        <th v-for="column in columns" :key="column.field">
          {{ column.header }}
        </th>
      </tr>
    </thead>
    <tbody v-if="sorted.length > 0">
      <tr v-for="(row, i) in sorted" :key="i">
        <td v-for="(field, j) in row" :key="j">
          <slot :name="field" :field="field" :row="row">
            {{ field }}
          </slot>
        </td>
      </tr>
    </tbody>
    <tbody v-else class="no-data">
      <tr>
        <td colspan="3">No Data</td>
      </tr>
    </tbody>
    <tfoot>
      <tr>
        <td :colspan="columns.length">
          <slot name="footer" />
        </td>
      </tr>
    </tfoot>
  </table>
</template>

<script setup lang="ts">
import { computed } from "vue";

type ColumnHeader = {
  field: string;
  header: string;
};

type RowData = {
  [field: string]: unknown;
};

const props = withDefaults(
  defineProps<{ columns?: ColumnHeader[]; rows?: RowData[] }>(),
  { rows: () => [], columns: () => [] }
);

const fields = computed(() => props.columns.map(col => col.field));
const sorted = computed(() =>
  props.rows.map(
    row =>
      Object.entries(row)
        .sort(
          ([aField], [bField]) =>
            fields.value.indexOf(aField) - fields.value.indexOf(bField)
        )
        .map(([field, value]) => value) // eslint-disable-line @typescript-eslint/no-unused-vars
  )
);
</script>

<style scoped>
table {
  background: var(--gray-100);
  color: var(--text-color);
  font-weight: 600;
  font-family: var(--font-family);
  border-spacing: 0px;
  text-align: left;
  width: 100%;
  table-layout: fixed;
  border-bottom: 1px solid white;
  font-size: 12px;
}
thead {
  font-size: 12px;
  background: var(--surface-hover);
}
th {
  padding: 0.3rem;
  color: var(--gray-800);
}
td {
  padding: 0.3rem 0.3rem;
  border-top: 1px solid var(--border-main);
  border-bottom: 1px solid var(--border-main);
  font-weight: 400;
  font-size: 14px;
}
tr > td:not(:first-child) {
  font-family: var(--monospace-family);
}

@media (max-width: 770px) {
  table {
    font-size: 7px;
  }
  thead,
  td {
    font-size: 7px;
  }
}
.no-data {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 70px;
}
</style>
